import { useState } from "react";
import { Plus, Pencil, Trash2, Loader2, FolderOpen, Check, X } from "lucide-react";
import {
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
} from "../hooks/useRepository";
import type { ResourceCategory } from "../types";

interface Props {
  categories: ResourceCategory[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Whether the current user may create new categories (coordinator / director). */
  canCreate: boolean;
}

export function CategorySidebar({ categories, selectedId, onSelect, canCreate }: Props) {
  const { mutateAsync: createCat, isPending: creating } = useCreateCategory();
  const { mutateAsync: updateCat, isPending: updating } = useUpdateCategory();
  const { mutateAsync: deleteCat, isPending: deleting } = useDeleteCategory();

  const [newName, setNewName] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  async function handleCreate() {
    if (!newName.trim()) return;
    await createCat({ name: newName.trim() });
    setNewName("");
    setShowNewForm(false);
  }

  async function handleUpdate(id: string) {
    if (!editName.trim()) return;
    await updateCat({ id, data: { name: editName.trim() } });
    setEditingId(null);
  }

  async function handleDelete(id: string) {
    if (!confirm("Usunąć kategorię? Możliwe tylko gdy nie ma w niej zasobów.")) return;
    try {
      await deleteCat(id);
      if (selectedId === id) onSelect(null);
    } catch {
      // error surfaced by query
    }
  }

  return (
    <aside className="flex flex-col w-full">
      <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-3 mb-2">
        Kategorie
      </p>

      {/* All */}
      <button
        onClick={() => onSelect(null)}
        className={`flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
          selectedId === null
            ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] dark:text-blue-300"
            : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium"
        }`}
      >
        <FolderOpen size={15} className={selectedId === null ? "text-[var(--color-primary)]" : "text-gray-400"} />
        Wszystkie
      </button>

      {categories.length > 0 && (
        <div className="my-2 border-t border-gray-100 dark:border-gray-800" />
      )}

      <div className="flex flex-col gap-0.5">
        {categories.map((cat) => (
          <div key={cat.id} className="group relative">
            {editingId === cat.id ? (
              <div className="flex items-center gap-1 px-2 py-1">
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleUpdate(cat.id);
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  autoFocus
                  className="flex-1 min-w-0 text-sm border border-[var(--color-primary)] rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <button onClick={() => handleUpdate(cat.id)} disabled={updating} className="flex-shrink-0 text-green-600 hover:text-green-700 p-0.5">
                  {updating ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                </button>
                <button onClick={() => setEditingId(null)} className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-0.5">
                  <X size={13} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => onSelect(cat.id)}
                className={`flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                  selectedId === cat.id
                    ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] dark:text-blue-300 font-semibold"
                    : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium"
                }`}
              >
                <FolderOpen size={15} className={`flex-shrink-0 ${selectedId === cat.id ? "text-[var(--color-primary)]" : "text-gray-400"}`} />
                <span className="flex-1 truncate">{cat.name}</span>

                {cat.can_manage && (
                  <span className="hidden group-hover:flex items-center gap-0.5">
                    <span
                      role="button"
                      onClick={(e) => { e.stopPropagation(); setEditingId(cat.id); setEditName(cat.name); }}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    >
                      <Pencil size={11} />
                    </span>
                    <span
                      role="button"
                      onClick={(e) => { e.stopPropagation(); handleDelete(cat.id); }}
                      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600"
                    >
                      {deleting ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                    </span>
                  </span>
                )}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add category */}
      {canCreate && (
        <div className="mt-3">
          {showNewForm ? (
            <div className="flex items-center gap-1 px-2 py-1">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                  if (e.key === "Escape") setShowNewForm(false);
                }}
                autoFocus
                placeholder="Nazwa kategorii"
                className="flex-1 min-w-0 text-sm border border-[var(--color-primary)] rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              <button onClick={handleCreate} disabled={creating} className="flex-shrink-0 text-green-600 hover:text-green-700 p-0.5">
                {creating ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              </button>
              <button onClick={() => setShowNewForm(false)} className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-0.5">
                <X size={13} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] dark:hover:text-blue-400 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 hover:border-[var(--color-primary)]/40 transition-colors"
            >
              <Plus size={12} />
              Dodaj kategorię
            </button>
          )}
        </div>
      )}
    </aside>
  );
}
